"use client"; // Required for Tiptap's client-side hooks

import React, { useEffect } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
  CircularProgress,
  Stack,
  ListItemButton,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  FormatAlignJustify,
} from "@mui/icons-material";

// Tiptap Imports
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import Underline from "@tiptap/extension-underline";

// Your App's Types and Components
import { Provider, QAType } from "utils/types/Provider.types"; // Adjust path as needed

// Define props for the editor
interface TiptapEditorProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * A reusable Tiptap editor component complete with a toolbar.
 * This is the component that replaces ReactQuill.
 */
const TiptapEditor: React.FC<TiptapEditorProps> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    // Synchronize the editor content if the prop value changes from outside
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  if (!editor) {
    return <CircularProgress />;
  }

  return (
    <Paper variant="outlined">
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ p: 1, borderBottom: "1px solid #ddd", flexWrap: "wrap" }}
      >
        <ToggleButtonGroup size="small" aria-label="text formatting">
          <ToggleButton
            value="bold"
            selected={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBold />
          </ToggleButton>
          <ToggleButton
            value="italic"
            selected={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalic />
          </ToggleButton>
          <ToggleButton
            value="underline"
            selected={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlined />
          </ToggleButton>
        </ToggleButtonGroup>
        <ToggleButtonGroup size="small" aria-label="text alignment">
          <ToggleButton
            value="left"
            selected={editor.isActive({ textAlign: "left" })}
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
          >
            <FormatAlignLeft />
          </ToggleButton>
          <ToggleButton
            value="center"
            selected={editor.isActive({ textAlign: "center" })}
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
          >
            <FormatAlignCenter />
          </ToggleButton>
          <ToggleButton
            value="right"
            selected={editor.isActive({ textAlign: "right" })}
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
          >
            <FormatAlignRight />
          </ToggleButton>
          <ToggleButton
            value="justify"
            selected={editor.isActive({ textAlign: "justify" })}
            onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          >
            <FormatAlignJustify />
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {/* Editor Content Area */}
      <EditorContent
        editor={editor}
        style={{ minHeight: "200px", padding: "16px", outline: "none" }}
      />
    </Paper>
  );
};

// --- ProviderDetails Component ---

interface ProviderDetailsProps {
  provider: Provider;
  isSaving: boolean;
  editDescription: string;
  editImage: string;
  editVideoUrl: string;
  editExternalUrl: string;
  editAdditionalInfo: string; // Corrected spelling
  setEditDescription: (val: string) => void;
  setEditImage: (val: string) => void;
  setEditVideoUrl: (val: string) => void;
  setEditExternalUrl: (val: string) => void;
  setEditAdditionalInfo: (val: string) => void; // Corrected spelling
  expandedQA: string | null;
  setExpandedQA: (id: string | null) => void;
  onSaveDetails: () => void;
  onAddQA: () => void;
  onEditQA: (qa: QAType) => void;
  onDeleteQA: (id: string) => void;
}

export const ProviderDetails: React.FC<ProviderDetailsProps> = ({
  provider,
  isSaving,
  editDescription,
  setEditDescription,
  editImage,
  setEditImage,
  editVideoUrl,
  setEditVideoUrl,
  editExternalUrl,
  setEditExternalUrl,
  editAdditionalInfo,
  setEditAdditionalInfo,
  expandedQA,
  setExpandedQA,
  onSaveDetails,
  onAddQA,
  onEditQA,
  onDeleteQA,
}) => {
  const handleExpand = (id: string) => {
    setExpandedQA(expandedQA === id ? null : id);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        {provider.name}
      </Typography>

      <Stack spacing={2.5}>
        {" "}
        {/* Increased spacing for better readability */}
        <Typography variant="h6">Description:</Typography>
        {/* Replaced ReactQuill with TiptapEditor */}
        <TiptapEditor value={editDescription} onChange={setEditDescription} />
        <TextField
          label="URL de l'image"
          value={editImage}
          onChange={(e) => setEditImage(e.target.value)}
          fullWidth
        />
        <TextField
          label="URL de la vidéo YouTube"
          value={editVideoUrl}
          onChange={(e) => setEditVideoUrl(e.target.value)}
          fullWidth
        />
        <TextField
          label="URL Externe (site du fournisseur)"
          value={editExternalUrl}
          onChange={(e) => setEditExternalUrl(e.target.value)}
          fullWidth
        />
        <Typography variant="h6">Information Additionnelle:</Typography>
        {/* Replaced ReactQuill with TiptapEditor */}
        <TiptapEditor
          value={editAdditionalInfo}
          onChange={setEditAdditionalInfo}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={onSaveDetails}
          disabled={isSaving}
          startIcon={
            isSaving ? <CircularProgress size={20} color="inherit" /> : null
          }
          sx={{ alignSelf: "flex-start" }}
        >
          {isSaving ? "Sauvegarde..." : "Sauvegarder les détails"}
        </Button>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
        >
          <Typography variant="h6">Questions & Réponses (Q&A)</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddQA}>
            Ajouter Q&A
          </Button>
        </Box>
        <List disablePadding>
          {(provider.qas || []).map((qa) => (
            <React.Fragment key={qa.id}>
              <ListItemButton
                onClick={() => handleExpand(qa.id!)}
                sx={{ borderBottom: "1px solid #eee" }}
              >
                <ListItemText primary={qa.question} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="edit"
                    onClick={() => onEditQA(qa)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => onDeleteQA(qa.id!)}
                  >
                    <DeleteIcon />
                  </IconButton>
                  <IconButton edge="end" aria-label="expand">
                    {expandedQA === qa.id ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItemButton>
              <Collapse in={expandedQA === qa.id} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "action.hover",
                    borderBottom: "1px solid #eee",
                    "& *": { margin: 0 }, // Reset margins for embedded HTML
                  }}
                >
                  {/* Use dangerouslySetInnerHTML to render the HTML */}
                  <Typography
                    variant="body2"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: qa.answer }}
                  />
                </Box>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Stack>
    </Box>
  );
};
